import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { useCallback, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { FormInput, FormTextarea, useGetFormData } from 'cng-common/features/form'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button/Button'
import { saveFile } from 'core/classes/helpers/saveFiles'
import { CrudStore } from 'core/types/crudStore'
import { UploadAttachments } from 'partner360/components/QBR/shared/reportIssue/components/reportModal/components/uploadAttachments/UploadAttachments'
import { IQBRReportIssueAttachment } from 'partner360/components/QBR/shared/reportIssue/components/reportModal/models'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import { IP360Ticket } from 'partner360/models/p360Ticket'

import {
  ATTACHMENT_FIELD_NAME,
  ATTACHMENT_TYPE,
  FORM_CODE_ATTACHMENTS,
  FORM_FOR_SAVING_ATTACHMENTS,
  initialData,
  QBR_REPORT_FORM_KEY,
} from './configs'

import styles from './QBRReportModalContent.module.scss'

interface IQBRErrorModalContentProps {
  onClose: () => void
}

export const QBRReportModalContent = ({ onClose }: IQBRErrorModalContentProps) => {
  const [wip, setWip] = useState(false)
  const [{ formData, isValid }] = useGetFormData(QBR_REPORT_FORM_KEY, initialData)

  const attachmentsCrud = CrudStore.get(
    CrudStoreItemPartner360.JBPPartnerAttachments,
    'reportIssue'
  )
  const ticketsCrud = CrudStore.get(CrudStoreItemPartner360.JBPTickets)

  const handleValidate = useCallback(async () => {
    const formValidation = FormValidationBuilder.build(QBR_REPORT_FORM_KEY)
    const errors = await Promise.all(
      [
        formValidation.call('description', formData?.description),
        formValidation.call('subject', formData?.subject),
        formValidation.call('attachments', formData?.attachments),
      ].filter((it) => it)
    )

    return errors.filter((it): it is TFormValidationResult => it !== undefined)
  }, [formData])

  const onSave = useCallback(async () => {
    const errors = await handleValidate()
    if (errors.length > 0 || !formData.attachments.length) {
      return
    }

    setWip(true)
    const dataForSaving = formData.attachments.map((fileData: IQBRReportIssueAttachment) => {
      return {
        Attachment: fileData.fileName,
        Type: ATTACHMENT_TYPE,
        file: fileData.file,
      }
    })

    const urls = await saveFile({
      formId: FORM_FOR_SAVING_ATTACHMENTS,
      fieldName: ATTACHMENT_FIELD_NAME,
      formCode: FORM_CODE_ATTACHMENTS,
      models: dataForSaving,
      source: attachmentsCrud,
    })

    const payload: IP360Ticket = {
      Description: formData.description,
      Subject: formData.subject,
      Links: JSON.stringify(urls),
      PageLink: window.location.href,
    }
    await ticketsCrud.create(1566, [payload])
    setWip(false)
    onClose()
  }, [
    attachmentsCrud,
    formData.attachments,
    formData.description,
    formData.subject,
    handleValidate,
    onClose,
    ticketsCrud,
  ])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={wip} />
      <div className={styles.title}>
        <Translation text={TRANSLATION_KEYS.reportIssueModal.title} />
      </div>
      <div className={styles.mainText}>
        <Translation text={TRANSLATION_KEYS.reportIssueModal.text} />
      </div>
      <div>
        <FormInput
          isRequired
          formKey={QBR_REPORT_FORM_KEY}
          field="subject"
          defaultValue={initialData.subject}
          type="text"
          label={TRANSLATION_KEYS.reportIssueModal.subject}
          margin={'md'}
        />
        <FormTextarea
          isRequired
          label={TRANSLATION_KEYS.reportIssueModal.body}
          defaultValue={initialData.description}
          field={'description'}
          formKey={QBR_REPORT_FORM_KEY}
          rows={5}
          margin={'md'}
        />
        <UploadAttachments
          acceptedFormats="image/*,text/plain"
          initialValue={initialData.attachments}
        />
      </div>
      <div className={styles.btnWrapper}>
        <Button onClick={onSave} margin={'md'} disabled={!isValid}>
          <Translation text={TRANSLATION_KEYS.reportIssueModal.save} />
        </Button>
        <Button onClick={onClose} margin={'md'} color={'danger'}>
          <Translation text={TRANSLATION_KEYS.reportIssueModal.cancel} />
        </Button>
      </div>
    </div>
  )
}
